import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Paper,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { marketAPI } from '../../services/api';

const EditMarketModal = ({ open, onClose, market, onSuccess }) => {
  const [formData, setFormData] = useState({
    market_name: '',
    open_time: '',
    close_time: '',
    open_digit: '',
    close_digit: '',
    open_panna: '',
    close_panna: '',
    market_status: 'active',
    market_available: true,
    market_active: true,
    schedule: {}
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  useEffect(() => {
    if (market && open) {
      setFormData({
        market_name: market.market_name || '',
        open_time: market.open_time || '',
        close_time: market.close_time || '',
        open_digit: market.open_digit || '',
        close_digit: market.close_digit || '',
        open_panna: market.open_panna || '',
        close_panna: market.close_panna || '',
        market_status: market.market_status || 'active',
        market_available: market.market_available ?? true,
        market_active: market.market_active ?? true,
        schedule: market.schedule || {}
      });
    }
  }, [market, open]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    setError('');
  };

  const handleScheduleChange = (day, field, value) => {
    setFormData({
      ...formData,
      schedule: {
        ...formData.schedule,
        [day]: {
          ...formData.schedule[day],
          [field]: value
        }
      }
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const updateData = {
        ...formData,
        open_digit: formData.open_digit ? parseInt(formData.open_digit) : null,
        close_digit: formData.close_digit ? parseInt(formData.close_digit) : null,
        open_panna: formData.open_panna ? parseInt(formData.open_panna) : null,
        close_panna: formData.close_panna ? parseInt(formData.close_panna) : null,
      };

      await marketAPI.updateMarket(market.id, updateData);
      onSuccess();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update market');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Edit Market - {market?.market_name}</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Basic Information
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Market Name"
              name="market_name"
              value={formData.market_name}
              onChange={handleChange}
              disabled={loading}
              required
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              label="Open Time"
              name="open_time"
              type="time"
              value={formData.open_time}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              disabled={loading}
              required
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              label="Close Time"
              name="close_time"
              type="time"
              value={formData.close_time}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              disabled={loading}
              required
            />
          </Grid>

          {/* Result Settings */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Result Settings
            </Typography>
          </Grid>

          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              label="Open Digit"
              name="open_digit"
              type="number"
              value={formData.open_digit}
              onChange={handleChange}
              disabled={loading}
              inputProps={{ min: 0, max: 9 }}
              helperText="0-9"
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              label="Close Digit"
              name="close_digit"
              type="number"
              value={formData.close_digit}
              onChange={handleChange}
              disabled={loading}
              inputProps={{ min: 0, max: 9 }}
              helperText="0-9"
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              label="Open Panna"
              name="open_panna"
              type="number"
              value={formData.open_panna}
              onChange={handleChange}
              disabled={loading}
              inputProps={{ min: 100, max: 999 }}
              helperText="100-999"
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              label="Close Panna"
              name="close_panna"
              type="number"
              value={formData.close_panna}
              onChange={handleChange}
              disabled={loading}
              inputProps={{ min: 100, max: 999 }}
              helperText="100-999"
            />
          </Grid>

          {/* Status Settings */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Status Settings
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Market Status</InputLabel>
              <Select
                name="market_status"
                value={formData.market_status}
                onChange={handleChange}
                label="Market Status"
                disabled={loading}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={4}>
            <FormControlLabel
              control={
                <Checkbox
                  name="market_available"
                  checked={formData.market_available}
                  onChange={handleChange}
                  disabled={loading}
                />
              }
              label="Market Available"
            />
          </Grid>

          <Grid item xs={6} md={4}>
            <FormControlLabel
              control={
                <Checkbox
                  name="market_active"
                  checked={formData.market_active}
                  onChange={handleChange}
                  disabled={loading}
                />
              }
              label="Market Active"
            />
          </Grid>

          {/* Schedule */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Weekly Schedule
            </Typography>
            <Grid container spacing={2}>
              {days.map((day) => (
                <Grid item xs={12} sm={6} md={4} key={day}>
                  <Paper sx={{ p: 2, backgroundColor: 'rgba(255,255,255,0.05)' }}>
                    <Typography variant="subtitle2" sx={{ textTransform: 'capitalize', mb: 1 }}>
                      {day}
                    </Typography>
                    
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.schedule[day]?.active ?? true}
                          onChange={(e) => handleScheduleChange(day, 'active', e.target.checked)}
                          disabled={loading}
                        />
                      }
                      label="Active"
                    />

                    {formData.schedule[day]?.active && (
                      <Box mt={1}>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="Open"
                              type="time"
                              size="small"
                              value={formData.schedule[day]?.open_time || ''}
                              onChange={(e) => handleScheduleChange(day, 'open_time', e.target.value)}
                              InputLabelProps={{ shrink: true }}
                              disabled={loading}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="Close"
                              type="time"
                              size="small"
                              value={formData.schedule[day]?.close_time || ''}
                              onChange={(e) => handleScheduleChange(day, 'close_time', e.target.value)}
                              InputLabelProps={{ shrink: true }}
                              disabled={loading}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? 'Updating...' : 'Update Market'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditMarketModal;
